import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Button } from './ui/button';
import { 
  Home, Briefcase, ShoppingBag, User, Settings, Plus,
  Monitor, Smartphone, Menu, X, Navigation, Phone, MessageCircle, Crown, Bell
} from 'lucide-react';

// Mobile detection
const isMobileDevice = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 768 || 
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
  return false;
};

// Mobile context
const MobileContext = createContext({
  isMobile: false,
  viewMode: 'auto',
  setViewMode: () => {},
  showMobileNav: true
});

export function useMobileView() {
  return useContext(MobileContext);
}

export function MobileProvider({ children }) {
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'mobile', 'desktop'
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = viewMode === 'mobile' || (viewMode === 'auto' && (windowWidth < 768 || isMobileDevice()));
  const showMobileNav = isMobile;

  return (
    <MobileContext.Provider value={{ isMobile, viewMode, setViewMode, showMobileNav, windowWidth }}>
      {children}
    </MobileContext.Provider>
  );
}

// View mode toggle button
export function ViewModeToggle({ className = '' }) {
  const { viewMode, setViewMode, isMobile } = useMobileView();

  const modes = [
    { value: 'auto', icon: Settings, label: 'Auto' },
    { value: 'mobile', icon: Smartphone, label: 'Mobile' },
    { value: 'desktop', icon: Monitor, label: 'Desktop' }
  ];

  return (
    <div className={`flex items-center gap-1 p-1 bg-gray-100 rounded-lg ${className}`} data-testid="view-mode-toggle">
      {modes.map(mode => {
        const Icon = mode.icon;
        const isActive = viewMode === mode.value;
        return (
          <button
            key={mode.value}
            onClick={() => setViewMode(mode.value)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-sm transition-colors ${
              isActive 
                ? 'bg-white shadow text-[#FF3008]' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title={mode.label}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{mode.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Notification badge component
function NotificationBadge({ count, className = '' }) {
  if (!count || count <= 0) return null;
  
  return (
    <span 
      className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full ${className}`}
      data-testid="notification-badge"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Bottom navigation bar for mobile
export function BottomNavBar() {
  const { user, isAuthenticated } = useAuth();
  const { showMobileNav } = useMobileView();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get notification counts
  let unreadCount = 0;
  let pendingJobsCount = 0;
  
  try {
    const notificationContext = useNotifications();
    unreadCount = notificationContext?.unreadCount || 0;
  } catch (e) {
    // NotificationContext might not be available
  }

  // Fetch pending jobs count for providers (would be from context or API)
  const [jobCounts, setJobCounts] = useState({ pending: 0, active: 0 });
  
  useEffect(() => {
    const fetchJobCounts = async () => {
      if (!isAuthenticated || !user) return;
      
      // Only fetch for providers
      if (!['mechanic', 'roadside_tech', 'admin', 'owner'].includes(user.user_type) && !user.is_admin && !user.is_owner) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/incoming-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // API returns array directly for incoming-requests
          const pendingCount = Array.isArray(data) ? data.length : (data.requests?.length || 0);
          setJobCounts({
            pending: pendingCount,
            active: 0  // Active jobs would need separate endpoint
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.log('Could not fetch job counts');
      }
    };
    
    fetchJobCounts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchJobCounts, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  // Hide on provider/mechanic pages since they have their own DoorDash-style tabs
  const isProviderPage = location.pathname.startsWith('/provider') || location.pathname.startsWith('/mechanic');
  if (!showMobileNav || !isAuthenticated || isProviderPage) return null;

  // Customer navigation - DoorDash style
  const customerNavItems = [
    { path: '/customer', icon: Home, label: 'Home' },
    { path: '/customer/request', icon: Plus, label: 'Get Help', highlight: true },
    { path: '/marketplace', icon: ShoppingBag, label: 'Shop' },
    { path: '/customer/history', icon: Briefcase, label: 'History' },
    { path: '/settings', icon: User, label: 'Account' }
  ];

  // Owner navigation
  const ownerNavItems = [
    { path: '/owner', icon: Crown, label: 'Command', badgeKey: 'pending' },
    { path: '/admin', icon: Home, label: 'Admin' },
    { path: '/marketplace', icon: ShoppingBag, label: 'Shop' },
    { path: '/settings', icon: User, label: 'Account' }
  ];

  // Admin navigation
  const adminNavItems = [
    { path: '/admin', icon: Home, label: 'Dashboard', badgeKey: 'pending' },
    { path: '/marketplace', icon: ShoppingBag, label: 'Shop' },
    { path: '/settings', icon: User, label: 'Account' }
  ];

  // Select nav items based on user type
  let navItems = customerNavItems;
  if (user?.is_owner || user?.user_type === 'owner') {
    navItems = ownerNavItems;
  } else if (user?.is_admin) {
    navItems = adminNavItems;
  }

  const colorClasses = {
    red: 'text-[#FF3008]',
    green: 'text-green-600',
    purple: 'text-purple-600',
    yellow: 'text-yellow-600',
    gray: 'text-gray-600'
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 safe-area-bottom"
      data-testid="bottom-nav"
    >
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const isHighlight = item.highlight;
          
          // Get badge count based on badge key
          const getBadgeCount = () => {
            if (item.badgeKey === 'pending') return jobCounts.pending;
            if (item.badgeKey === 'active') return jobCounts.active;
            if (item.badgeKey === 'notifications') return unreadCount;
            return 0;
          };
          const badgeCount = getBadgeCount();
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${
                isHighlight 
                  ? 'relative -mt-4' 
                  : ''
              } ${
                isActive 
                  ? colorClasses[item.color] || 'text-[#FF3008]'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              {isHighlight ? (
                <div className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
                  isActive ? 'bg-[#FF3008]' : 'bg-[#FF3008]/90'
                }`}>
                  <Icon className="w-7 h-7 text-white" />
                  <NotificationBadge count={badgeCount} className="-top-1 -right-1" />
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Icon className={`w-6 h-6 ${isActive ? 'scale-110' : ''} transition-transform`} />
                    <NotificationBadge count={badgeCount} />
                  </div>
                  <span className="text-xs mt-1 font-medium">{item.label}</span>
                </>
              )}
              {isHighlight && (
                <span className={`text-xs mt-1 font-medium ${isActive ? 'text-[#FF3008]' : 'text-gray-500'}`}>
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Sticky bottom action bar for job pages
export function BottomActionBar({ children, className = '' }) {
  const { showMobileNav } = useMobileView();
  
  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40 p-4 ${
        showMobileNav ? 'mb-16' : ''
      } ${className}`}
      data-testid="bottom-action-bar"
    >
      <div className="max-w-lg mx-auto flex gap-3">
        {children}
      </div>
    </div>
  );
}

// Quick action buttons (Call, Message, Navigate)
export function QuickActionButtons({ 
  phone, 
  onMessage, 
  navigationUrl,
  showLabels = true,
  size = 'md'
}) {
  const sizeClasses = {
    sm: 'h-10 px-3 text-sm',
    md: 'h-12 px-4',
    lg: 'h-14 px-6 text-lg'
  };

  return (
    <div className="flex gap-2" data-testid="quick-actions">
      {phone && (
        <Button
          variant="outline"
          className={`flex-1 ${sizeClasses[size]} border-green-200 hover:bg-green-50`}
          onClick={() => window.location.href = `tel:${phone}`}
          data-testid="quick-call-btn"
        >
          <Phone className="w-5 h-5 text-green-600" />
          {showLabels && <span className="ml-2">Call</span>}
        </Button>
      )}
      
      {onMessage && (
        <Button
          variant="outline"
          className={`flex-1 ${sizeClasses[size]} border-[#FF3008]/20 hover:bg-red-50`}
          onClick={onMessage}
          data-testid="quick-message-btn"
        >
          <MessageCircle className="w-5 h-5 text-[#FF3008]" />
          {showLabels && <span className="ml-2">Message</span>}
        </Button>
      )}
      
      {navigationUrl && (
        <Button
          className={`flex-1 ${sizeClasses[size]} bg-[#FF3008] hover:bg-[#E02800]`}
          onClick={() => window.open(navigationUrl, '_blank')}
          data-testid="quick-navigate-btn"
        >
          <Navigation className="w-5 h-5" />
          {showLabels && <span className="ml-2">Navigate</span>}
        </Button>
      )}
    </div>
  );
}

// Responsive container with proper padding for mobile
export function ResponsiveContainer({ children, className = '', noPadding = false }) {
  const { isMobile, showMobileNav } = useMobileView();
  
  return (
    <div 
      className={`
        ${noPadding ? '' : 'px-4 sm:px-6 lg:px-8'}
        ${showMobileNav ? 'pb-20' : 'pb-4'}
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// Mobile-friendly card with larger touch targets
export function MobileCard({ children, onClick, className = '' }) {
  const { isMobile } = useMobileView();
  
  return (
    <div 
      onClick={onClick}
      className={`
        rounded-lg border bg-white shadow-sm
        ${onClick ? 'cursor-pointer active:bg-gray-50' : ''}
        ${isMobile ? 'p-4' : 'p-6'}
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// ETA display component
export function ETADisplay({ etaMinutes, etaText, distanceMiles, status, className = '' }) {
  if (status === 'arrived' || status === 'in_progress' || status === 'completed') {
    return (
      <div className={`flex items-center gap-2 text-green-600 ${className}`}>
        <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
        <span className="font-medium">
          {status === 'arrived' ? 'Provider Arrived' : 
           status === 'in_progress' ? 'Service In Progress' : 
           'Service Completed'}
        </span>
      </div>
    );
  }

  if (!etaMinutes) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        Calculating ETA...
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <div className="text-center">
        <p className="text-3xl font-bold text-[#FF3008]">{etaMinutes}</p>
        <p className="text-xs text-gray-500 uppercase">min away</p>
      </div>
      {distanceMiles && (
        <div className="text-center border-l pl-4">
          <p className="text-lg font-semibold text-gray-700">{distanceMiles}</p>
          <p className="text-xs text-gray-500 uppercase">miles</p>
        </div>
      )}
    </div>
  );
}

export default MobileProvider;
